/**
 * useSendMessage Hook - Send messages using orchestrator service
 * This is a basic implementation that can be extended/overridden by specific apps
 */
import { useCallback } from "react";
import { useConversation } from "../context/ConversationContext";
// Global service instance - should be set by app-specific code
let messageService = null;
let abortControllerRef = null;
// Callback to mark all in-progress items as stopped (set by sendMessage, called by stopExecution)
let markAllStoppedCallback = null;
/**
 * Set the message service implementation
 * This should be called by app-specific code during initialization
 */
export function setMessageService(service) {
    messageService = service;
}
/**
 * Get the current message service (for cleanup/debugging)
 */
export function getMessageService() {
    return messageService;
}
/**
 * Set or update the message enhancer
 * This allows planning mode to inject automated reminders dynamically
 */
export function setMessageEnhancer(enhancer) {
    if (messageService) {
        messageService.messageEnhancer = enhancer;
        console.log('[useSendMessage] Message enhancer', enhancer ? 'set' : 'cleared');
    }
    else {
        console.warn('[useSendMessage] Cannot set enhancer - message service not initialized');
    }
}
/**
 * Get the current message enhancer (for testing/debugging)
 */
export function getMessageEnhancer() {
    return messageService?.messageEnhancer;
}
/**
 * Notify the message service that a new conversation was created
 * This allows the app-specific code to register the conversation with backend services
 */
export async function notifyNewConversation(conversationId) {
    if (messageService?.onNewConversation) {
        await messageService.onNewConversation(conversationId);
    }
}
export const useSendMessage = () => {
    const { conversationId, addMessage, updateLastMessageComponents, setLoading, setError, messages } = useConversation();
    const sendMessage = useCallback(async (message, options) => {
        const isSilent = options?.silent ?? false;
        if (!conversationId) {
            if (!isSilent)
                setError("No conversation initialized");
            console.error('[useSendMessage] No conversation initialized');
            return;
        }
        if (!messageService) {
            if (!isSilent)
                setError("Message service not initialized. Please initialize the orchestrator service.");
            console.error('[useSendMessage] Message service not set');
            return;
        }
        try {
            // Only set loading state for non-silent messages (chat UI)
            if (!isSilent) {
                setLoading(true);
            }
            if (!isSilent) {
                setError(null);
            }
            // Create abort controller for this request
            abortControllerRef = new AbortController();
            const signal = abortControllerRef.signal;
            // Add messages to chat UI only if not silent
            if (!isSilent) {
                // Add user message
                addMessage({
                    role: "user",
                    content: message,
                    userImageAttachments: options?.userImageAttachments,
                    mentions: options?.mentions,
                    html: options?.html,
                });
                // Add empty assistant message that we'll update
                addMessage({ role: "assistant", content: "" });
            }
            // Log silent mode for debugging
            if (isSilent) {
                console.log('[useSendMessage] Silent mode - not adding messages to chat UI');
            }
            const segments = [];
            // Current text being accumulated (will become a segment when tool starts)
            let currentTextContent = "";
            // Track tool data for progressive UI
            const toolDataMap = new Map();
            // Track taskpane-visible latency per top-level orchestrator tool call
            const toolStartVisibleAtMsByToolId = new Map();
            const toolFirstResponseLoggedToolIds = new Set();
            const logToolStartVisible = (toolName, toolId) => {
                const startAt = Date.now();
                toolStartVisibleAtMsByToolId.set(toolId, startAt);
                console.log(`[TaskPane Latency][${toolName}] start_visible_at=${startAt}`);
            };
            const logToolFirstResponseVisible = (toolName, toolId, source) => {
                if (toolFirstResponseLoggedToolIds.has(toolId)) {
                    return;
                }
                const startAt = toolStartVisibleAtMsByToolId.get(toolId);
                if (typeof startAt !== 'number') {
                    return;
                }
                const now = Date.now();
                const ttftMs = now - startAt;
                toolFirstResponseLoggedToolIds.add(toolId);
                console.log(`[TaskPane Latency][${toolName}] first_response_visible_at=${now} ttft_ms=${ttftMs} source=${source}`);
            };
            // Helper to recursively mark all tools as stopped
            const markToolsStopped = (tool) => {
                if (tool.status !== 'complete' && tool.status !== 'error') {
                    tool.status = 'stopped';
                }
                if ('segments' in tool && tool.segments) {
                    for (const seg of tool.segments) {
                        if (seg.type === 'tool' && seg.toolData) {
                            markToolsStopped(seg.toolData);
                        }
                    }
                }
            };
            // Callback to mark all in-progress items as stopped (called by stopExecution)
            markAllStoppedCallback = () => {
                console.log('[useSendMessage] Marking all in-progress tools as stopped');
                for (const segment of segments) {
                    if (segment.type === 'tool' && segment.toolData) {
                        markToolsStopped(segment.toolData);
                    }
                }
                // Update UI one last time to show stopped state
                updateLastMessageComponents(buildComponents());
            };
            // Wrapper to conditionally update UI components (skipped in silent mode)
            const updateUIComponents = (components) => {
                if (!isSilent) {
                    updateLastMessageComponents(components);
                }
            };
            // Build components array from segments
            const buildComponents = () => {
                const components = [];
                let order = 0;
                // Process all segments in order
                for (const segment of segments) {
                    if (segment.type === 'text' && segment.textContent) {
                        components.push({
                            msg_component_id: segment.id,
                            type: 'text',
                            content: segment.textContent,
                            order: order++,
                        });
                    }
                    else if (segment.type === 'tool' && segment.toolData) {
                        // Special handling for userInterview tool
                        if (segment.toolData.name === 'userInterview') {
                            // Extract questions from the fields
                            const questionsField = segment.toolData.fields.find(f => f.name === 'questions');
                            const questions = questionsField?.value || [];
                            // Create interview data for the UI component
                            const interviewData = {
                                id: segment.toolData.id,
                                questions: questions,
                                currentQuestionIndex: 0,
                                answers: [],
                                status: segment.toolData.status === 'complete'
                                    ? (segment.toolData.error ? 'cancelled' : 'completed')
                                    : 'active',
                            };
                            components.push({
                                msg_component_id: `interview-${segment.toolData.id}`,
                                type: 'user_interview',
                                content: JSON.stringify(interviewData),
                                displayVerb: 'Clarifying Question',
                                order: order++,
                            });
                        }
                        else if (segment.toolData.name === 'reorderOrDeleteSlides') {
                            // Special handling for reorderOrDeleteSlides tool
                            const operationsField = segment.toolData.fields.find(f => f.name === 'operations');
                            const rawOperations = operationsField?.value || [];
                            const resultStatus = typeof segment.toolData.result === "object" &&
                                segment.toolData.result &&
                                "status" in segment.toolData.result
                                ? segment.toolData.result.status
                                : undefined;
                            // Wrap raw operations in PendingOperation format for the UI component
                            // The UI expects op.operation.action, not op.action directly
                            const pendingOperations = rawOperations.map((op, idx) => ({
                                index: idx,
                                operation: op, // Wrap the raw operation
                                resolvedSlideId: op.slideId || `slide_${op.slideIndex}`,
                                currentIndex: op.slideIndex || 0,
                                slideTitle: undefined, // Not available during streaming
                                slideImage: undefined, // Not available during streaming
                                requiresApproval: op.action === 'delete',
                                approved: op.action === 'move' ? true : undefined, // Auto-approve moves
                            }));
                            // Create approval data for the UI component
                            const approvalData = {
                                id: segment.toolData.id,
                                operations: pendingOperations,
                                currentOperationIndex: 0,
                                status: segment.toolData.status === 'complete'
                                    ? (resultStatus === 'timeout'
                                        ? 'timeout'
                                        : resultStatus === 'cancelled' || segment.toolData.error
                                            ? 'cancelled'
                                            : 'approved')
                                    : 'pending',
                            };
                            components.push({
                                msg_component_id: `slide-ops-${segment.toolData.id}`,
                                type: 'slide_operations_approval',
                                content: JSON.stringify(approvalData),
                                displayVerb: 'Slide Operations',
                                order: order++,
                            });
                        }
                        else {
                            // Regular tool handling
                            components.push({
                                msg_component_id: `tool-${segment.toolData.id}`,
                                type: segment.toolData.status === 'complete' ? 'tool_complete' : 'tool_start',
                                content: JSON.stringify(segment.toolData),
                                displayVerb: segment.toolData.name,
                                order: order++,
                            });
                        }
                    }
                }
                // Add current streaming text (after all segments)
                if (currentTextContent) {
                    components.push({
                        msg_component_id: 'response-streaming',
                        type: 'text',
                        content: currentTextContent,
                        order: order++,
                    });
                }
                return components;
            };
            const upsertField = (fields, fieldName, value) => {
                const existingIndex = fields.findIndex(field => field.name === fieldName);
                if (existingIndex >= 0) {
                    fields[existingIndex] = {
                        name: fieldName,
                        value,
                        timestamp: Date.now(),
                    };
                    return;
                }
                fields.push({
                    name: fieldName,
                    value,
                    timestamp: Date.now(),
                });
            };
            // Operation callbacks for progressive UI
            const operationCallbacks = {
                onToolStart: (toolName, _hasStreaming, isAgent) => {
                    if (signal.aborted)
                        return;
                    // INTERLEAVING: Freeze current text as a segment before adding tool
                    if (currentTextContent.trim()) {
                        segments.push({
                            type: 'text',
                            id: `text-${Date.now()}-${segments.length}`,
                            textContent: currentTextContent,
                        });
                        currentTextContent = ""; // Start fresh for text after tool
                    }
                    const toolId = `${toolName}-${Date.now()}`;
                    const toolData = {
                        id: toolId,
                        name: toolName,
                        status: 'starting',
                        fields: [],
                        isAgent: isAgent,
                    };
                    toolDataMap.set(toolId, toolData);
                    // Add tool as a segment
                    segments.push({
                        type: 'tool',
                        id: toolId,
                        toolData: toolData,
                    });
                    console.log(`[useSendMessage] Tool started: ${toolName}, segments: ${segments.length}`);
                    logToolStartVisible(toolName, toolId);
                    updateUIComponents(buildComponents());
                },
                onOperationField: (toolName, fieldName, value) => {
                    if (signal.aborted)
                        return;
                    // Find the tool data (most recent one with this name)
                    let targetToolData;
                    Array.from(toolDataMap.values()).forEach(td => {
                        if (td.name === toolName) {
                            targetToolData = td;
                        }
                    });
                    if (targetToolData) {
                        targetToolData.status = 'streaming';
                        upsertField(targetToolData.fields, fieldName, value);
                        logToolFirstResponseVisible(toolName, targetToolData.id, 'operation_field');
                        console.log(`[useSendMessage] Field parsed: ${toolName}.${fieldName}`);
                        updateUIComponents(buildComponents());
                    }
                },
                onToolResult: (toolName, result) => {
                    if (signal.aborted)
                        return;
                    // Find and update the most recent unfinished tool with this name
                    const toolDataValues = Array.from(toolDataMap.values());
                    for (let i = toolDataValues.length - 1; i >= 0; i--) {
                        const td = toolDataValues[i];
                        if (td.name === toolName && td.status !== 'complete') {
                            td.status = 'complete';
                            td.result = result;
                            break;
                        }
                    }
                    console.log(`[useSendMessage] Tool complete: ${toolName}`);
                    updateUIComponents(buildComponents());
                },
                // Agent streaming text - accumulate to currentText
                // Now handles nested agents: "linkedExcelEditorAgent/editChartAgent"
                onAgentChunk: (agentPath, chunk) => {
                    if (signal.aborted)
                        return;
                    // Parse the path: "parent" or "parent/nested"
                    const pathParts = agentPath.split('/');
                    const rootAgentName = pathParts[0];
                    const nestedPath = pathParts.slice(1);
                    // Find the most recent root agent in toolDataMap
                    let targetAgent;
                    const toolDataValues = Array.from(toolDataMap.values());
                    for (let i = toolDataValues.length - 1; i >= 0; i--) {
                        const td = toolDataValues[i];
                        if (td.name === rootAgentName && td.isAgent) {
                            targetAgent = td;
                            break;
                        }
                    }
                    if (!targetAgent)
                        return;
                    targetAgent.status = 'executing';
                    if (nestedPath.length === 0) {
                        // Direct chunk for root agent
                        targetAgent.currentText = (targetAgent.currentText || '') + chunk;
                    }
                    else {
                        // Chunk for nested agent - find the nested agent segment
                        const nestedAgentName = nestedPath[0];
                        if (targetAgent.segments) {
                            // Find the nested agent's segment (most recent matching tool segment)
                            for (let i = targetAgent.segments.length - 1; i >= 0; i--) {
                                const seg = targetAgent.segments[i];
                                if (seg.type === 'tool' && seg.toolData?.name === nestedAgentName && seg.toolData.isAgent) {
                                    seg.toolData.currentText = (seg.toolData.currentText || '') + chunk;
                                    break;
                                }
                            }
                        }
                    }
                    updateUIComponents(buildComponents());
                },
                // Internal tool starts - freeze current text as segment, add tool segment
                // Now handles nested agents and passes isAgent flag
                onAgentToolStart: (agentName, internalToolName, _hasOperation, isAgent) => {
                    if (signal.aborted)
                        return;
                    // Parse tool name for nested paths: "editChartAgent/editChart"
                    const toolParts = internalToolName.split('/');
                    const directToolName = toolParts[0];
                    const nestedToolPath = toolParts.slice(1);
                    const toolDataValues = Array.from(toolDataMap.values());
                    for (let i = toolDataValues.length - 1; i >= 0; i--) {
                        const td = toolDataValues[i];
                        if (td.name === agentName && td.isAgent) {
                            if (!td.segments) {
                                td.segments = [];
                            }
                            if (nestedToolPath.length === 0) {
                                // Direct tool for root agent
                                // INTERLEAVE: Freeze current text as a segment before adding tool
                                if (td.currentText && td.currentText.trim()) {
                                    td.segments.push({
                                        type: 'text',
                                        id: `text-${Date.now()}-${td.segments.length}`,
                                        textContent: td.currentText,
                                        timestamp: Date.now(),
                                    });
                                    td.currentText = ''; // Start fresh for text after tool
                                }
                                // Create internal tool data with isAgent flag
                                const internalTool = {
                                    id: `${directToolName}-${Date.now()}`,
                                    name: directToolName,
                                    fields: [],
                                    status: 'streaming',
                                    isAgent: isAgent, // Mark if this is a nested agent
                                    segments: isAgent ? [] : undefined, // Nested agents have segments
                                    currentText: isAgent ? '' : undefined, // Nested agents have text
                                };
                                // Add tool as a segment
                                td.segments.push({
                                    type: 'tool',
                                    id: internalTool.id,
                                    toolData: internalTool,
                                    timestamp: Date.now(),
                                });
                                console.log(`[useSendMessage] Agent ${agentName} tool starting: ${directToolName}, isAgent: ${isAgent}, segments: ${td.segments.length}`);
                            }
                            else {
                                // Nested tool - find the nested agent and add to its segments
                                const nestedAgentName = directToolName;
                                const actualToolName = nestedToolPath.join('/');
                                for (let i = td.segments.length - 1; i >= 0; i--) {
                                    const seg = td.segments[i];
                                    if (seg.type === 'tool' && seg.toolData?.name === nestedAgentName && seg.toolData.isAgent) {
                                        // Found the nested agent - freeze its text and add the tool
                                        if (!seg.toolData.segments) {
                                            seg.toolData.segments = [];
                                        }
                                        if (seg.toolData.currentText && seg.toolData.currentText.trim()) {
                                            seg.toolData.segments.push({
                                                type: 'text',
                                                id: `text-${Date.now()}-${seg.toolData.segments.length}`,
                                                textContent: seg.toolData.currentText,
                                                timestamp: Date.now(),
                                            });
                                            seg.toolData.currentText = '';
                                        }
                                        const nestedTool = {
                                            id: `${actualToolName}-${Date.now()}`,
                                            name: actualToolName,
                                            fields: [],
                                            status: 'streaming',
                                            isAgent: isAgent,
                                        };
                                        seg.toolData.segments.push({
                                            type: 'tool',
                                            id: nestedTool.id,
                                            toolData: nestedTool,
                                            timestamp: Date.now(),
                                        });
                                        console.log(`[useSendMessage] Nested agent ${nestedAgentName} tool starting: ${actualToolName}`);
                                        break;
                                    }
                                }
                            }
                            break;
                        }
                    }
                    updateUIComponents(buildComponents());
                },
                // Internal tool field parsed during streaming
                // Now handles nested paths
                onAgentOperationField: (agentName, internalToolName, fieldName, value) => {
                    if (signal.aborted)
                        return;
                    const toolParts = internalToolName.split('/');
                    const toolDataValues = Array.from(toolDataMap.values());
                    for (let i = toolDataValues.length - 1; i >= 0; i--) {
                        const td = toolDataValues[i];
                        if (td.name === agentName && td.isAgent && td.segments) {
                            if (toolParts.length === 1) {
                                // Direct tool field
                                for (let i = td.segments.length - 1; i >= 0; i--) {
                                    const seg = td.segments[i];
                                    if (seg.type === 'tool' && seg.toolData?.name === internalToolName && seg.toolData.status === 'streaming') {
                                        if (!seg.toolData.fields) {
                                            seg.toolData.fields = [];
                                        }
                                        upsertField(seg.toolData.fields, fieldName, value);
                                        console.log(`[useSendMessage] Agent ${agentName} field: ${internalToolName}.${fieldName}`);
                                        break;
                                    }
                                }
                            }
                            else {
                                // Nested tool field
                                const nestedAgentName = toolParts[0];
                                const actualToolName = toolParts.slice(1).join('/');
                                for (let i = td.segments.length - 1; i >= 0; i--) {
                                    const seg = td.segments[i];
                                    if (seg.type === 'tool' && seg.toolData?.name === nestedAgentName && seg.toolData.isAgent && seg.toolData.segments) {
                                        for (let j = seg.toolData.segments.length - 1; j >= 0; j--) {
                                            const nestedSeg = seg.toolData.segments[j];
                                            if (nestedSeg.type === 'tool' && nestedSeg.toolData?.name === actualToolName && nestedSeg.toolData.status === 'streaming') {
                                                if (!nestedSeg.toolData.fields) {
                                                    nestedSeg.toolData.fields = [];
                                                }
                                                upsertField(nestedSeg.toolData.fields, fieldName, value);
                                                console.log(`[useSendMessage] Nested agent ${nestedAgentName} field: ${actualToolName}.${fieldName}`);
                                                break;
                                            }
                                        }
                                        break;
                                    }
                                }
                            }
                            break;
                        }
                    }
                    updateUIComponents(buildComponents());
                },
                // Internal tool args fully parsed, ready to execute
                // Now handles nested paths
                onAgentToolCall: (agentName, internalToolName, args) => {
                    if (signal.aborted)
                        return;
                    const toolParts = internalToolName.split('/');
                    const toolDataValues = Array.from(toolDataMap.values());
                    for (let i = toolDataValues.length - 1; i >= 0; i--) {
                        const td = toolDataValues[i];
                        if (td.name === agentName && td.isAgent && td.segments) {
                            if (toolParts.length === 1) {
                                // Direct tool
                                for (let i = td.segments.length - 1; i >= 0; i--) {
                                    const seg = td.segments[i];
                                    if (seg.type === 'tool' && seg.toolData?.name === internalToolName &&
                                        (seg.toolData.status === 'streaming' || !seg.toolData.args)) {
                                        seg.toolData.args = args;
                                        seg.toolData.status = 'calling';
                                        console.log(`[useSendMessage] Agent ${agentName} calling: ${internalToolName}`);
                                        break;
                                    }
                                }
                            }
                            else {
                                // Nested tool
                                const nestedAgentName = toolParts[0];
                                const actualToolName = toolParts.slice(1).join('/');
                                for (let i = td.segments.length - 1; i >= 0; i--) {
                                    const seg = td.segments[i];
                                    if (seg.type === 'tool' && seg.toolData?.name === nestedAgentName && seg.toolData.isAgent && seg.toolData.segments) {
                                        for (let j = seg.toolData.segments.length - 1; j >= 0; j--) {
                                            const nestedSeg = seg.toolData.segments[j];
                                            if (nestedSeg.type === 'tool' && nestedSeg.toolData?.name === actualToolName &&
                                                (nestedSeg.toolData.status === 'streaming' || !nestedSeg.toolData.args)) {
                                                nestedSeg.toolData.args = args;
                                                nestedSeg.toolData.status = 'calling';
                                                console.log(`[useSendMessage] Nested agent ${nestedAgentName} calling: ${actualToolName}`);
                                                break;
                                            }
                                        }
                                        break;
                                    }
                                }
                            }
                            break;
                        }
                    }
                    updateUIComponents(buildComponents());
                },
                // Internal tool returned result
                // Now handles nested paths
                onAgentToolResult: (agentName, internalToolName, result) => {
                    if (signal.aborted)
                        return;
                    const toolParts = internalToolName.split('/');
                    const toolDataValues = Array.from(toolDataMap.values());
                    for (let i = toolDataValues.length - 1; i >= 0; i--) {
                        const td = toolDataValues[i];
                        if (td.name === agentName && td.isAgent && td.segments) {
                            if (toolParts.length === 1) {
                                // Direct tool result
                                for (let i = td.segments.length - 1; i >= 0; i--) {
                                    const seg = td.segments[i];
                                    if (seg.type === 'tool' && seg.toolData?.name === internalToolName && seg.toolData.status === 'calling') {
                                        seg.toolData.status = 'complete';
                                        seg.toolData.result = result;
                                        console.log(`[useSendMessage] Agent ${agentName} got result from: ${internalToolName}`);
                                        break;
                                    }
                                }
                            }
                            else {
                                // Nested tool result
                                const nestedAgentName = toolParts[0];
                                const actualToolName = toolParts.slice(1).join('/');
                                for (let i = td.segments.length - 1; i >= 0; i--) {
                                    const seg = td.segments[i];
                                    if (seg.type === 'tool' && seg.toolData?.name === nestedAgentName && seg.toolData.isAgent && seg.toolData.segments) {
                                        for (let j = seg.toolData.segments.length - 1; j >= 0; j--) {
                                            const nestedSeg = seg.toolData.segments[j];
                                            if (nestedSeg.type === 'tool' && nestedSeg.toolData?.name === actualToolName && nestedSeg.toolData.status === 'calling') {
                                                nestedSeg.toolData.status = 'complete';
                                                nestedSeg.toolData.result = result;
                                                console.log(`[useSendMessage] Nested agent ${nestedAgentName} got result from: ${actualToolName}`);
                                                break;
                                            }
                                        }
                                        break;
                                    }
                                }
                            }
                            break;
                        }
                    }
                    updateUIComponents(buildComponents());
                },
            };
            // Enhance message with automated reminders if enhancer is configured
            const enhancedMessage = messageService.messageEnhancer
                ? messageService.messageEnhancer(message)
                : message;
            // Log if message was enhanced (for debugging)
            if (enhancedMessage !== message) {
                console.log('[useSendMessage] Message enhanced with automated reminders');
            }
            // Send message using service with streaming callback and operation callbacks
            const response = await messageService.sendMessage(enhancedMessage, conversationId, (chunk) => {
                // Check if aborted before processing chunk
                if (signal.aborted)
                    return;
                // Accumulate to current text segment (will be added to segments when tool starts)
                currentTextContent += chunk;
                updateUIComponents(buildComponents());
            }, operationCallbacks, options?.context ? { context: options.context } : undefined);
            // Check if aborted
            if (signal.aborted) {
                console.log('[useSendMessage] Request was aborted');
                return;
            }
            // Final update with complete response
            // Freeze any remaining text as a final segment
            if (currentTextContent.trim()) {
                segments.push({
                    type: 'text',
                    id: `text-final-${Date.now()}`,
                    textContent: currentTextContent,
                });
                currentTextContent = "";
            }
            const finalComponents = buildComponents();
            if (finalComponents.length === 0 && response?.content) {
                // Ensure we have at least the response content (if available)
                finalComponents.push({
                    msg_component_id: `response-${Date.now()}`,
                    type: 'text',
                    content: response.content,
                    order: 0,
                });
            }
            updateUIComponents(finalComponents);
            if (!isSilent) {
                setLoading(false);
            }
            // Clear the stopped callback since request completed successfully
            markAllStoppedCallback = null;
        }
        catch (err) {
            if (abortControllerRef?.signal.aborted) {
                console.log('[useSendMessage] Request was aborted');
                // Note: markAllStoppedCallback already called by stopExecution
                return;
            }
            const errorMsg = err instanceof Error ? err.message : "Failed to send message";
            console.error('[useSendMessage] Error:', errorMsg, err);
            if (!isSilent) {
                setError(errorMsg);
                setLoading(false);
            }
            // Clear the stopped callback since request completed with error
            markAllStoppedCallback = null;
        }
    }, [conversationId, addMessage, updateLastMessageComponents, setLoading, setError]);
    const stopExecution = useCallback(() => {
        console.log('[useSendMessage] 🛑 Stop execution requested');
        // Mark all in-progress tools as stopped in the UI
        if (markAllStoppedCallback) {
            markAllStoppedCallback();
            markAllStoppedCallback = null;
        }
        // Abort the UI signal (stops callback updates)
        if (abortControllerRef) {
            abortControllerRef.abort();
            abortControllerRef = null;
        }
        // Cancel the task in the agent registry (stops actual execution)
        if (messageService?.cancel && conversationId) {
            console.log('[useSendMessage] 🛑 Cancelling task in registry for conversation:', conversationId);
            messageService.cancel(conversationId);
        }
        setLoading(false);
    }, [setLoading, conversationId]);
    const resetConversation = useCallback(() => {
        console.log('[useSendMessage] 🔄 Reset conversation requested');
        if (abortControllerRef) {
            abortControllerRef.abort();
            abortControllerRef = null;
        }
    }, []);
    /**
     * Send a message without adding to chat UI.
     * Use this for inline form operations where processing happens in-place.
     */
    const sendSilentMessage = useCallback(async (message) => {
        return sendMessage(message, { silent: true });
    }, [sendMessage]);
    return { sendMessage, sendSilentMessage, stopExecution, resetConversation };
};
